const API_BASE_CANDIDATES = resolveApiBaseCandidates();
let activeApiBase = API_BASE_CANDIDATES[0];
const REQUEST_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export async function loadResources(token) {
  const fallback = emptyResources();
  const resourceRequests = [
    ["products", api("/products", { token })],
    ["recommendations", api("/recommendations", { token })],
    ["pricing", api("/pricing", { token })],
    ["pricingHistory", api("/pricing-history", { token })],
    ["orders", api("/orders", { token })],
    ["outstanding", api("/outstanding", { token })],
    ["targets", api("/targets", { token })],
    ["expenses", api("/expenses", { token })],
    ["plans", api("/plans", { token })],
    ["gpsExceptions", api("/gps-exceptions", { token })],
    ["fieldTracking", api("/field-tracking", { token })],
    ["routes", api("/routes", { token })],
    ["employeeDailyTelemetry", api("/employee-daily-telemetry", { token })],
    ["tickets", api("/tickets", { token })],
    ["callLogs", api("/call-logs", { token })],
    ["communications", api("/communications", { token })],
    ["faqs", api("/faqs", { token })],
    ["messageTemplates", api("/message-templates", { token })],
    ["incentives", api("/incentives", { token })],
    ["channelPartners", api("/channel-partners", { token })],
    ["channelIncentives", api("/channel-incentives", { token })],
    ["activityCustomFields", api("/activity-custom-fields", { token })],
    ["dropdownOptions", api("/dropdown-options", { token })],
    ["hiddenCustomers", api("/customers/hidden", { token })],
    ["customerDeletionRequests", api("/customer-deletion-requests", { token })],
    ["auditLogs", api("/audit-logs", { token })],
    ["lifecycle", api("/lifecycle", { token })],
    ["fullReport", api("/reports/full", { token })],
    ["adminOverview", api("/admin/overview", { token })],
  ];

  const settled = await Promise.allSettled(resourceRequests.map(([, request]) => request));
  const resources = { ...fallback };
  const failedSections = [];
  settled.forEach((result, index) => {
    const [key] = resourceRequests[index];
    if (result.status === "fulfilled") {
      const normalized = normalizeResourceValue(key, result.value, fallback[key]);
      if (!normalized.valid) {
        failedSections.push(key);
        return;
      }
      resources[key] = normalized.value;
      return;
    }
    if (Number(result.reason?.status || 0) === 401) throw result.reason;
    if (key === "adminOverview" && [403, 404].includes(Number(result.reason?.status || 0))) return;
    failedSections.push(key);
  });
  if (failedSections.length) {
    resources.loadWarning = buildResourceLoadWarning(failedSections);
  }
  resources.failedSections = failedSections;
  resources.failedSectionLabels = failedSections.map((key) => resourceLabels[key] || "CRM data").filter(Boolean);
  return resources;
}

export function emptyResources() {
  return {
    products: [],
    recommendations: [],
    pricing: [],
    pricingHistory: [],
    orders: [],
    outstanding: [],
    targets: [],
    expenses: [],
    plans: [],
    gpsExceptions: [],
    fieldTracking: {},
    routes: [],
    employeeDailyTelemetry: { employees: [], usageEvents: [], routes: [], openVisits: [] },
    tickets: [],
    callLogs: [],
    communications: [],
    faqs: [],
    messageTemplates: [],
    incentives: [],
    channelPartners: [],
    channelIncentives: [],
    activityCustomFields: [],
    dropdownOptions: [],
    hiddenCustomers: [],
    customerDeletionRequests: [],
    auditLogs: [],
    lifecycle: {},
    fullReport: {},
    adminOverview: null,
    loadWarning: "",
    failedSections: [],
    failedSectionLabels: [],
  };
}

export async function api(path, options = {}) {
  const bases = [activeApiBase, ...API_BASE_CANDIDATES.filter((base) => base !== activeApiBase)];
  let lastError;
  for (const base of bases) {
    try {
      const data = await requestApi(base, path, options);
      activeApiBase = base;
      return data;
    } catch (error) {
      lastError = error;
      if (hasConfiguredApiBase() || options.disableApiBaseFallback || !shouldTryNextApiBase(error)) break;
    }
  }
  throw lastError;
}

async function requestApi(apiBase, path, options = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(`${apiBase}${path}`, {
      method: options.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      cache: options.cache ?? "no-store",
      signal: controller.signal,
    });
  } catch (error) {
    throw createNetworkApiError(error);
  } finally {
    window.clearTimeout(timeoutId);
  }
  const data = await parseApiJson(response);
  if (!response.ok) {
    throw createResponseApiError(response, data);
  }
  return data;
}

function resolveApiBaseCandidates() {
  const configuredBase = readConfiguredApiBase();
  if (configuredBase) return [configuredBase];

  const origin = window.location.origin;
  const prefixedPath = window.location.pathname.match(/^\/(crmai\d+)(?=\/|$)/i);
  return unique([
    `${origin}/api`,
    prefixedPath ? `${origin}/${prefixedPath[1]}/api` : "",
    `${origin}/crmai1/api`,
  ]);
}

function readConfiguredApiBase() {
  const configured = window.__CRM_API_BASE__ || document.querySelector("meta[name='crm-api-base']")?.content || "";
  return normalizeApiBase(configured);
}

function hasConfiguredApiBase() {
  return Boolean(readConfiguredApiBase());
}

function normalizeApiBase(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const url = trimmed.startsWith("http")
    ? trimmed
    : `${window.location.origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
  return url.replace(/\/+$/, "");
}

function shouldTryNextApiBase(error) {
  const status = Number(error?.status || 0);
  if (status === 0) return true;
  return Boolean(error?.data?._nonJson) && [200, 404].includes(status);
}

function unique(values) {
  return [...new Set(values.filter(Boolean).map((value) => value.replace(/\/+$/, "")))];
}

export function userFriendlyErrorMessage(error) {
  if (!error) return "Something went wrong. Please try again.";
  if (error instanceof ApiError) return error.message;
  if (error.name === "AbortError") return "The CRM is taking longer than expected. Please try again.";
  return "We could not complete that action. Please try again.";
}

export function readSession(scope = "demo") {
  try {
    const session = JSON.parse(localStorage.getItem(sessionStorageKey(scope)));
    if (!session?.token || !session?.user?.id) return null;
    return session;
  } catch {
    return null;
  }
}

export function saveSession(session, scope = "demo") {
  try {
    localStorage.setItem(sessionStorageKey(scope), JSON.stringify(session));
    return true;
  } catch {
    return false;
  }
}

export function clearSession(scope = "demo") {
  try {
    localStorage.removeItem(sessionStorageKey(scope));
  } catch {
    // The current tab can still clear in-memory state even if storage is unavailable.
  }
}

function sessionStorageKey(scope = "demo") {
  return scope && scope !== "demo" ? `crmSession:${scope}` : "crmSession";
}

function createNetworkApiError(error) {
  const message =
    error?.name === "AbortError"
      ? "The CRM is taking longer than expected. Please try again."
      : "We could not reach the CRM server. Check your connection and try again.";
  return new ApiError(message, { status: 0, data: {}, cause: error });
}

async function parseApiJson(response) {
  const text = await response.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    if (response.ok) {
      throw new ApiError("The CRM returned an unexpected response. Refresh and try again.", {
        status: response.status,
        data: { _nonJson: true },
      });
    }
    return { _nonJson: true };
  }
}

function createResponseApiError(response, data = {}) {
  return new ApiError(responseErrorMessage(response.status, data.error), {
    status: response.status,
    data,
  });
}

function normalizeResourceValue(key, value, fallbackValue) {
  const resourceValidator = resourceValidators[key];
  if (resourceValidator && !resourceValidator(value)) {
    return { value: fallbackValue, valid: false };
  }
  if (Array.isArray(fallbackValue)) {
    return Array.isArray(value) ? { value, valid: true } : { value: fallbackValue, valid: false };
  }
  if (fallbackValue && typeof fallbackValue === "object") {
    return value && typeof value === "object" && !Array.isArray(value)
      ? { value, valid: true }
      : { value: fallbackValue, valid: false };
  }
  if (fallbackValue === null) {
    return value === null || (value && typeof value === "object" && !Array.isArray(value))
      ? { value, valid: true }
      : { value: fallbackValue, valid: false };
  }
  return { value: value ?? fallbackValue, valid: true };
}

const resourceValidators = {
  employeeDailyTelemetry: isValidEmployeeDailyTelemetry,
};

const resourceLabels = {
  products: "Product catalog",
  recommendations: "Recommendations",
  pricing: "Customer pricing",
  pricingHistory: "Pricing history",
  orders: "Orders",
  outstanding: "Collections",
  targets: "Targets",
  expenses: "Expenses",
  plans: "Visit plans",
  gpsExceptions: "GPS exceptions",
  fieldTracking: "Field tracking",
  routes: "Travel routes",
  employeeDailyTelemetry: "Employee telemetry",
  tickets: "Support tickets",
  callLogs: "Call logs",
  communications: "Messaging",
  faqs: "FAQs",
  messageTemplates: "Message templates",
  incentives: "Incentives",
  channelPartners: "Channel partners",
  channelIncentives: "Channel incentives",
  activityCustomFields: "Custom activity fields",
  dropdownOptions: "Dropdown options",
  hiddenCustomers: "Hidden prospects",
  customerDeletionRequests: "Prospect deletion requests",
  auditLogs: "Audit trail",
  lifecycle: "Customer lifecycle",
  fullReport: "Reports",
  adminOverview: "Admin setup",
};

function buildResourceLoadWarning(failedSections) {
  const labels = formatResourceLabels(failedSections);
  const detail = labels ? ` Affected areas: ${labels}.` : "";
  return `Some CRM sections could not refresh. You can keep working with the data shown and try again in a moment.${detail}`;
}

function formatResourceLabels(failedSections) {
  const labels = [...new Set(failedSections.map((key) => resourceLabels[key] || "CRM data"))];
  if (!labels.length) return "";
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.slice(0, 3).join(", ")}, and ${labels.length - 3} more`;
}

function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function isValidEmployeeDailyTelemetry(value) {
  if (
    !isPlainObject(value) ||
    !Array.isArray(value.employees) ||
    !Array.isArray(value.usageEvents) ||
    !Array.isArray(value.routes) ||
    !Array.isArray(value.openVisits)
  ) {
    return false;
  }
  return (
    value.employees.every(isPlainObject) &&
    value.usageEvents.every(isPlainObject) &&
    value.openVisits.every(isPlainObject) &&
    value.routes.every((route) => isPlainObject(route) && (!route.points || Array.isArray(route.points)) && (route.points || []).every(isPlainObject))
  );
}

function responseErrorMessage(status, serverMessage = "") {
  if (status >= 500) return "The CRM server had trouble completing this request. Please try again.";
  if (status === 429) return "The CRM is receiving many requests. Wait a moment and try again.";
  if (status === 426) return serverMessage || "A secure HTTPS connection is required. Reopen the CRM using the secure website link.";
  if (status === 408) return "The request took too long. Check your connection and try again.";
  if (status === 409) return serverMessage || "This record changed while you were working. Refresh and try again.";
  if (status === 422) return serverMessage || "Some details could not be accepted. Review them and try again.";
  if (status === 404) return serverMessage || "We could not find that record. Refresh and try again.";
  if (status === 403) return serverMessage || "You do not have permission to do this.";
  if (status === 401) return serverMessage || "Your session expired. Please sign in again.";
  if (status === 413) return "The information is too large to save. Shorten it and try again.";
  if (status === 415) return "This request could not be sent correctly. Refresh and try again.";
  return serverMessage || "We could not complete that action. Please check the details and try again.";
}
